/**
 * Solmar: gift box upsell in the cart drawer.
 *
 * The cart is the single source of truth: "gift box selected" = either gift box
 * variant is in the cart. The "+" button adds it; the offer card is then rendered
 * hidden by Liquid, and comes back when the customer removes the gift box line.
 * While it is in the cart, exactly one unit of the right variant must be there:
 *   qualifying subtotal <  threshold → regular variant
 *   qualifying subtotal >= threshold → promo variant
 *
 * When the check runs:
 * - "+" click → add the right variant, then verify against Shopify's response.
 * - Every drawer re-render (any add / remove / quantity change that goes through
 *   the theme drawer) and every page load → read the JSON snapshot printed by
 *   the snippet. No extra request unless the cart is wrong.
 * - Cart changes that don't re-render the drawer (cart page, other scripts) →
 *   seen passively via PerformanceObserver, then one GET /cart.js, only while a
 *   gift box is in the cart.
 *
 * Fixes are one /cart/update.js call (remove wrong line + add right variant
 * together), queued one at a time. The drawer is redrawn with the theme's own
 * CartDrawer.renderContents(). On the cart page the page reloads after a swap.
 * Our own requests carry ?sm_gift=1 so they never trigger another check (no loops).
 */
if (!customElements.get('sm-gift-box')) {
  const OWN_REQUEST = 'sm_gift=1';
  const CART_WRITE_PATH = /\/cart\/(add|change|update|clear)(\.js)?\/?$/;

  const state = {
    queue: Promise.resolve(),
    config: null,
    hasGift: false,
    lastRender: 0,
    failedSignature: '',
    observerTimer: null
  };

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const isCartPage = () =>
    (window.theme && theme.pageType === 'cart') || /\/cart\/?$/.test(window.location.pathname);
  const liveElement = () => document.querySelector('sm-gift-box');

  /* ---------- Cart logic (pure) ---------- */

  function isGiftLine(item, cfg) {
    return item.variant_id === cfg.regularId || (cfg.promoId > 0 && item.variant_id === cfg.promoId);
  }

  function qualifyingSubtotal(cart, cfg) {
    return cart.items.reduce((sum, item) => {
      if (isGiftLine(item, cfg) || item.gift_card) return sum;
      return sum + Number(cfg.mode === 'final' ? item.final_line_price : item.original_line_price);
    }, 0);
  }

  function targetVariant(cart, cfg) {
    if (!cfg.promoId || !cfg.thresholdCents) return cfg.regularId;
    // Threshold is set in store currency; cart prices are in the customer's currency.
    const rate = Number(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
    const threshold = Math.round(cfg.thresholdCents * rate);
    return qualifyingSubtotal(cart, cfg) >= threshold ? cfg.promoId : cfg.regularId;
  }

  /**
   * Returns the cart/update.js "updates" object that brings the cart to the
   * wanted state, or null when the cart is already correct.
   */
  function planUpdates(cart, cfg, wantGift) {
    const lines = cart.items.filter((item) => isGiftLine(item, cfg));
    const updates = {};

    if (!wantGift) {
      lines.forEach((line) => { updates[line.key] = 0; });
    } else {
      const target = targetVariant(cart, cfg);
      const keep = lines.find((line) => line.variant_id === target);

      lines.forEach((line) => {
        if (line === keep) {
          if (line.quantity !== 1) updates[line.key] = 1;
        } else {
          updates[line.key] = 0;
        }
      });

      if (!keep) updates[target] = 1;
    }

    return Object.keys(updates).length ? updates : null;
  }

  function cartHasGift(cart, cfg) {
    return cart.items.some((item) => isGiftLine(item, cfg));
  }

  function signature(cart, cfg) {
    const giftLines = cart.items.filter((item) => isGiftLine(item, cfg)).map((item) => [item.variant_id, item.quantity]);
    return JSON.stringify([giftLines, qualifyingSubtotal(cart, cfg)]);
  }

  /* ---------- Network ---------- */

  async function getCart() {
    const response = await fetch(`${root()}cart.js?${OWN_REQUEST}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('');
    return response.json();
  }

  async function postUpdates(updates) {
    const drawerEl = document.getElementById('cart-drawer');
    const sectionId = drawerEl && !isCartPage() ? drawerEl.dataset.id : null;
    const body = { updates };

    if (sectionId) {
      body.sections = [sectionId];
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(`${root()}cart/update.js?${OWN_REQUEST}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || '');
    }

    return { cart: data, sectionId };
  }

  function renderDrawer({ cart, sectionId }) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!sectionId || !cart.sections || !cartDrawer || typeof cartDrawer.renderContents !== 'function') return;

    window.cartTotal = cart.total_price;
    // Same renderer the theme uses after quantity changes; doesn't open the drawer.
    cartDrawer.renderContents(cart, false, null, '', true);
    document.dispatchEvent(new CustomEvent('sm:gift-box:updated', { detail: { cart } }));
  }

  /**
   * Brings the cart to the wanted state.
   * wantGift: true from the "+" button; undefined = keep whatever is in the cart.
   */
  async function sync(cfg, startCart, wantGift) {
    let cart = startCart || (await getCart());
    const want = typeof wantGift === 'boolean' ? wantGift : cartHasGift(cart, cfg);
    let changed = false;

    for (let attempt = 0; attempt < 2; attempt++) {
      const updates = planUpdates(cart, cfg, want);
      if (!updates) return { cart, changed };

      try {
        const result = await postUpdates(updates);
        cart = result.cart;
        changed = true;
        renderDrawer(result);
      } catch (error) {
        // First failure may be stale line keys: re-read the real cart and try once more.
        if (attempt === 0) {
          cart = await getCart();
          continue;
        }
        throw error;
      }
    }

    if (planUpdates(cart, cfg, want)) throw new Error('');
    return { cart, changed };
  }

  /* ---------- Task queue (one cart request at a time) ---------- */

  function setLoading(isLoading) {
    const el = liveElement();
    if (el) el.setLoading(isLoading);

    const drawerEl = document.getElementById('cart-drawer');
    if (drawerEl) drawerEl.classList.toggle('cart-drawer--disabled', isLoading);
  }

  function runTask(task, wantGift) {
    state.queue = state.queue
      .then(async () => {
        const cfg = state.config;
        if (!cfg) return;

        let errorMessage = '';
        let changed = false;

        setLoading(true);

        try {
          const result = await task(cfg);
          changed = result.changed;
          state.hasGift = cartHasGift(result.cart, cfg);
          state.failedSignature = '';
        } catch (error) {
          errorMessage = (error && error.message) || (wantGift ? cfg.soldOutText : cfg.errorText);

          // Re-read the real cart and stop automatic retries for this exact state.
          try {
            const cart = await getCart();
            state.hasGift = cartHasGift(cart, cfg);
            state.failedSignature = signature(cart, cfg);
          } catch (e) { /* network down: leave as is */ }
        } finally {
          setLoading(false);
        }

        if (changed && isCartPage()) {
          // The cart page has its own renderer; reload once so it shows the swapped variant.
          window.location.reload();
          return;
        }

        if (errorMessage) {
          const el = liveElement();
          if (el) el.showError(errorMessage);
        }
      })
      .catch(() => {});

    return state.queue;
  }

  /* ---------- Cart changes that don't re-render the drawer ---------- */

  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver((list) => {
        const hit = list.getEntries().find((entry) => {
          if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return false;
          if (entry.name.includes(OWN_REQUEST)) return false;
          try {
            return CART_WRITE_PATH.test(new URL(entry.name, window.location.origin).pathname);
          } catch (e) {
            return false;
          }
        });
        if (!hit) return;

        const seenAt = hit.responseEnd;
        clearTimeout(state.observerTimer);
        state.observerTimer = setTimeout(() => {
          if (!state.config || !state.hasGift) return;
          // The drawer already re-rendered with the new cart → connectedCallback handled it.
          if (state.lastRender > seenAt) return;

          runTask(async (cfg) => {
            const cart = await getCart();
            if (signature(cart, cfg) === state.failedSignature) return { cart, changed: false };
            return sync(cfg, cart);
          });
        }, 400);
      }).observe({ type: 'resource' });
    } catch (e) { /* older browsers: drawer re-renders still cover the drawer */ }
  }

  /* ---------- Element ---------- */

  class SmGiftBox extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-gift-add]');
      this.errorEl = this.querySelector('[data-gift-error]');

      const cfg = {
        regularId: Number(this.dataset.regularId) || 0,
        promoId: Number(this.dataset.promoId) || 0,
        thresholdCents: Number(this.dataset.thresholdCents) || 0,
        mode: this.dataset.subtotalMode === 'final' ? 'final' : 'original',
        errorText: this.dataset.errorText || '',
        soldOutText: this.dataset.soldOutText || ''
      };
      if (!cfg.regularId) return;

      state.config = cfg;
      state.lastRender = performance.now();

      // Only present while the gift box isn't in the cart (the card is hidden otherwise).
      if (this.button) {
        this.handleAdd = this.handleAdd.bind(this);
        this.button.addEventListener('click', this.handleAdd);
      }

      // Page load or drawer re-render: fix the variant if this render shows the wrong one.
      const snapshot = this.readSnapshot();
      if (!snapshot) return;

      state.hasGift = cartHasGift(snapshot, cfg);

      if (
        state.hasGift &&
        planUpdates(snapshot, cfg, true) &&
        signature(snapshot, cfg) !== state.failedSignature
      ) {
        runTask((config) => sync(config, snapshot));
      }
    }

    disconnectedCallback() {
      if (this.button && this.handleAdd) this.button.removeEventListener('click', this.handleAdd);
    }

    readSnapshot() {
      const script = this.querySelector('[data-gift-cart]');
      if (!script) return null;
      try {
        const data = JSON.parse(script.textContent);
        return Array.isArray(data.items) ? data : null;
      } catch (e) {
        return null;
      }
    }

    handleAdd(event) {
      event.preventDefault();
      // Ignore repeated clicks while a cart request is running.
      if (this.button.disabled) return;

      this.showError('');
      this.setLoading(true);

      runTask((cfg) => {
        const el = liveElement();
        const snapshot = el ? el.readSnapshot() : null;
        return sync(cfg, snapshot, true);
      }, true);
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      if (this.button) this.button.disabled = isLoading;
    }

    showError(message) {
      if (this.errorEl) this.errorEl.textContent = message || '';
    }
  }

  customElements.define('sm-gift-box', SmGiftBox);
}
